import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { v4 as uuidv4 } from 'uuid';

import TimeContainer from './TimeContainer';

const useStyles = makeStyles()(() => ({
  main: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  timeContainer: {
    display: 'flex',
    marginTop: '20px',
  },
  button: {
    marginTop: 'auto!important',
  },
}));

interface Day {
  id: string;
  day: string;
  selected: boolean;
  identifier: string;
  __typename?: string;
}

interface BusinessHoursData {
  days: Day[];
  hours: [any, any][];
  place: string;
  __typename: string;
  id: string;
}

const BLANK_BUSINESS_HOURS: BusinessHoursData = {
  days: [
    { id: '1', day: 'L', selected: false, identifier: '1' },
    { id: '2', day: 'M', selected: false, identifier: '2' },
    { id: '3', day: 'M', selected: false, identifier: '3' },
    { id: '4', day: 'J', selected: false, identifier: '4' },
    { id: '5', day: 'V', selected: false, identifier: '5' },
    { id: '6', day: 'S', selected: false, identifier: '6' },
    { id: '7', day: 'D', selected: false, identifier: '7' },
  ],
  hours: [[null, null]],
  place: '',
  __typename: 'OpeningHour',
  id: uuidv4(),
};

function hasPlace(initData: BusinessHoursData[] | null): boolean {
  let result = false;
  if (initData !== null) {
    initData.forEach((data) => {
      if (data.place !== '') {
        result = true;
      }
    });
  }
  return result;
}

interface SchedulerContainerProps {
  onChange: (openingHours: any[]) => void;
  initData?: BusinessHoursData[];
  [key: string]: any;
}

const SchedulerContainer = ({ onChange, initData, ...other }: SchedulerContainerProps) => {
  const { classes } = useStyles();
  const [currentId, setCurrentId] = useState<string | null>(null);

  const [availableDays, setAvailableDays] = useState<BusinessHoursData>(BLANK_BUSINESS_HOURS);
  const [timeFrames, setTimeFrames] = useState<any[]>(
    initData !== undefined
      ? initData.map((data) => {
        return [data.days, data.hours, data.place, data.id];
      })
      : [],
  );
  const [showPlace, setShowPlace] = useState(
    initData !== undefined && hasPlace(initData),
  );

  const [timeContainerList, setTimeContainerList] = useState<BusinessHoursData[]>(
    initData !== undefined ? initData : [],
  );

  const firstUpdate = useRef(true);

  const addTimeContainer = () => {
    const newId = uuidv4();
    const NEW_BLANK_BUSINESS_HOURS = { ...BLANK_BUSINESS_HOURS };
    NEW_BLANK_BUSINESS_HOURS.id = newId;
    const addTimeFrame = ({ days, hours, place, id }: BusinessHoursData) => {
      const newTimeFrame = [days, hours, place, newId];
      const timeFrameList = [...timeFrames, newTimeFrame];
      setTimeFrames(timeFrameList);
    };
    addTimeFrame(NEW_BLANK_BUSINESS_HOURS);

    const newTimeContainerList = [
      ...timeContainerList,
      NEW_BLANK_BUSINESS_HOURS,
    ];
    setTimeContainerList(newTimeContainerList);
  };

  const updateWeekDays = (daysList: any) => {
    setAvailableDays(daysList);
  };

  const updateTimeFrames = (timeFrame: any, indexTimeContainer: string) => {
    if (timeFrames !== undefined && timeFrames.length !== 0) {
      let newTimeFramesList = [...timeFrames];

      newTimeFramesList = [...newTimeFramesList].map((currentTimeFrame) => {
        return indexTimeContainer === currentTimeFrame[3]
          ? timeFrame
          : currentTimeFrame;
      });

      setTimeFrames(newTimeFramesList);
    }
  };

  const deleteTimeContainer = (e: any, index: string) => {
    const newTimeFramesList = [...timeFrames].filter((currentTimeFrame) => {
      return index !== currentTimeFrame[3];
    });

    const newTimeContainerList = [...timeContainerList].filter(
      (currentTimeFrame) => {
        return index !== currentTimeFrame.id;
      },
    );

    setTimeFrames(newTimeFramesList);
    setTimeContainerList(newTimeContainerList);
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;

      addTimeContainer();
      if (initData !== undefined) {
        deleteTimeContainer(null, timeContainerList.length.toString());
      }
      return;
    }

    let hasOpeningHour = false;

    const openingHours = timeFrames.map((timeFrame) => {
      const openingHour: any = {};
      openingHour.days = timeFrame[0];
      openingHour.days.map((day: Day) => {
        delete (day as any).__typename;
        if (day.selected) {
          hasOpeningHour = true;
        }
        return 'ok';
      });
      openingHour.hours = timeFrame[1];
      openingHour.place = timeFrame[2];
      return openingHour;
    });
    if (hasOpeningHour) {
      onChange(openingHours);
    } else {
      onChange([]);
    }
  }, [timeFrames]);

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <div>
          <FormControlLabel
            control={
              <Switch
                checked={showPlace}
                onChange={() => setShowPlace(!showPlace)}
                name="Unique Place"
              />
            }
            label={
              showPlace
                ? 'Cacher les adresses'
                : 'Indiquer des adresses'
            }
          />
        </div>

        {timeContainerList.length > 0 &&
          timeContainerList.map(({ days, hours, place, id }) => {
            return (
              <div className={classes.timeContainer} key={id}>
                <TimeContainer
                  updateWeekDays={updateWeekDays}
                  updateTimeFrames={updateTimeFrames}
                  availableDays={days}
                  indexTimeContainer={id}
                  deleteTimeContainer={deleteTimeContainer}
                  inputPlace={place}
                  showPlace={showPlace}
                  initData={hours}
                />
              </div>
            );
          })}
      </div>
      <Button
        className={classes.button}
        onClick={addTimeContainer}
        disableRipple
      >
        <AddCircleOutlineIcon fontSize="small" />
      </Button>
    </div>
  );
};

export default SchedulerContainer;
